import Banner from '@/components/atoms/Banner.vue'
import Button from '@/components/atoms/Button.vue'
import Checkbox from '@/components/atoms/Checkbox.vue'
import Input from '@/components/atoms/Input.vue'
import Spinner from '@/components/atoms/Spinner.vue'
import '@/components/global'

import AddItemToCart from '@/components/organisms/cart/add-item/AddItemToCart.vue'

import loadBundleDiscount from '@/directives/load-bundle-discount'
import sticky from '@/directives/sticky'
import { buildReplaceReferralUrl } from '@/services/referral/replace-referral-url'
import { EventForThirdPartySubscriptionFacadeFactory } from '@/usecases/event-bus-for-third-party/subscriber/facade'

import { HlAppMode } from '@/models/hl-app/hl-app-mode'
import { adCodeStore } from '@/util/adcode/store'
import AppId from '@/util/appid'
import { getZealsMeta } from '@/util/html-meta'
import '@/validation/email-compatible-with-old-system'

import BrandPanel from '@ajax/vue/components/atoms/BrandPanel.vue'
import Dropdown from '@ajax/vue/components/atoms/Dropdown.vue'
import LinkPanel from '@ajax/vue/components/atoms/LinkPanel.vue'
import MoreLink from '@ajax/vue/components/atoms/MoreLink.vue'
import Overlay from '@ajax/vue/components/atoms/Overlay.vue'
import RangeSlider from '@ajax/vue/components/atoms/RangeSlider.vue'
import ErrorModalContainer from '@ajax/vue/components/organisms/ErrorModalContainer.vue'
import ModalFull from '@ajax/vue/components/organisms/ModalFull.vue'
import SearchDropdown from '@ajax/vue/components/organisms/SearchDropdown.vue'
import ConditionChild from '@ajax/vue/components/templates/item-list/condition/default/ConditionChild.vue'
import ConditionDropdown from '@ajax/vue/components/templates/item-list/condition/default/select/ConditionDropdown.vue'
import ConditionRadio from '@ajax/vue/components/templates/item-list/condition/default/select/ConditionRadio.vue'
import spaStore from '@spa/store'

import objectFitImages from 'object-fit-images'

import 'url-search-params-polyfill'
import VeeValidate, { Validator } from 'vee-validate'
import ja from 'vee-validate/dist/locale/ja'
import Vue from 'vue'
import VueHead from 'vue-head'
import Notifications from 'vue-notification'
import VueRouter from 'vue-router'
import Initiator from './modules/initiator'

import router from './router'
import footerRouter from './router/footer'
import headerRouter from './router/header'
import headerBottomUserNotificationRouter from './router/header-bottom-user-notification'
import previewNotificationRouter from './router/previewNotification'
import autoRedirect from './vue/directives/auto-redirect'

import store from './vue/store'

/**
 * AjaxとSPAに関して、
 * Webpackで出力されるエンドポイント（バンドルされたファイル）は、ajax用とspa用に分かれている。
 * ただしajax用のエンドポイントにはajaxフォルダ（およびその依存）のみが含まれるが、spa用のエンドポイントにはajaxフォルダとspaフォルダ（およびそれらの依存）が含まれる。
 * そのため、SPA実行時にも、同じ一つのjsバンドルファイル内でこのinitAjaxメソッドは実行される。
 * - とくにたとえば、ここで生成されたシングルトンインスタンスは、ajaxフォルダのコードでもspaフォルダのコードからでも、同じものにアクセスできる。
 * このWebpackの設定自体は容易には変更しないものとして前提してよいため、ajaxエンドポイント（MPAページ）とspa共エンドポイント（SPAページ）に共通する初期処理をこのinitAjaxメソッドに書くことは構わない。
 * しかしやはりinitAjaxというajax側のフォルダにあるコードに、ajax/spaの共通処理が存在しうることは非直感的であるため、コメントしておく。
 * 将来的には、共通の初期化処理をより中立的な名前空間に移すことを検討したい。
 */

async function initAjax(config: {
  isHlAppWebViewUa: boolean,
}) {
  new EventForThirdPartySubscriptionFacadeFactory()
    .createForHost(AppId.getHostId())
    .subscribeAll()

  Initiator.initSession()

  Initiator.initAdCode()

  if (config.isHlAppWebViewUa) {
    document.body.classList.add('hl_app')
  }

  // なるべく早めに発火させるため先頭に持ってきている。
  /**
   * 事前にAdCodeを読み込んでおく必要がある。
   * この依存関係が暗黙になってしまっているため、pub/sub方式などで明示的に呼び出し順序を保証してもよいが、それほど激しく変更が入る箇所でもないのでそこまでのケアをしていない。
   * もしくはAdCode#loadがコストのない操作（idempotentで、かつ何度呼び出しても性能的に問題ない）と信じられるならば、あえてここで改めてloadしてもよいかもしれない。
   * - AdCodeの実装はそうなっていてその内容はおそらく容易に変わらないと想像できるが、I/Fとしてそこまで信じられるかといえば微妙なので、コメントに留める。
   */
  Initiator.publishEveryPageViewEvent(adCodeStore.read().toUndefined())

  Vue.directive('auto-redirect', autoRedirect)
  Vue.directive('object-fit-images', {
    inserted (el) {
      objectFitImages(el)
    },
  })
  Vue.directive('sticky', sticky)
  Vue.directive('loadBundleDiscount', loadBundleDiscount)
  Vue.use(VueHead)
  Vue.use(VueRouter)

  // TODO: グローバルに登録するべきコンポーネントと、不要なコンポーネントを整理
  // グローバルでは不要なコンポーネントを登録しているコードはすべて削除する.
  Vue.component('ajax-input', Input)
  Vue.component('ajax-spinner', Spinner)
  Vue.component('ajax-brand-panel', BrandPanel)
  Vue.component('ajax-button', Button)
  Vue.component('ajax-checkbox', Checkbox)
  Vue.component('ajax-dropdown', Dropdown)
  Vue.component('ajax-condition-dropdown', ConditionDropdown)
  Vue.component('ajax-condition-radio', ConditionRadio)
  Vue.component('ajax-search-dropdown', SearchDropdown)
  Vue.component('ajax-link-panel', LinkPanel)
  Vue.component('ajax-morelink', MoreLink)
  Vue.component('ajax-overlay', Overlay)
  Vue.component('ajax-rangeslider', RangeSlider)
  Vue.component('ajax-modalfull', ModalFull)
  Vue.component('ajax-conditionchild', ConditionChild)
  Vue.component('ajax-error-modal-container', ErrorModalContainer)
  Vue.component('ajax-incart', AddItemToCart)
  Vue.component('ajax-banner', Banner)

  Vue.use(Notifications)

  Vue.use(VeeValidate)
  Validator.localize('ja', ja)

  // This is workaround before SPA/Ajax are integrated
  if (isOnSpa()) {
    if (!config.isHlAppWebViewUa) {
      // tslint:disable-next-line:no-unused-expression
      new Vue({
        el: '#vue-footer-frame',
        router: footerRouter,
      })
    }

    await commonInitializer()
  } else {
    // tslint:disable-next-line:no-unused-expression
    new Vue({
      el: '#ajax-app',
      head: {
        meta: [
          ...getZealsMeta(),
        ],
      },
      router,
      store,
    })

    if (!config.isHlAppWebViewUa) {
      // tslint:disable-next-line:no-unused-expression
      new Vue({
        el: '#vue-footer-frame',
        router: footerRouter,
      })

      // tslint:disable-next-line:no-unused-expression
      new Vue({
        el: '#vue-ec-header',
        store: spaStore,
        router: headerRouter,
      })

      // tslint:disable-next-line:no-unused-expression
      new Vue({
        el: '#vue-header-bottom-user-notification',
        store: spaStore,
        router: headerBottomUserNotificationRouter,
      })
    }

    // tslint:disable-next-line:no-unused-expression
    new Vue({
      el: '#vue-third-party-tool',
      store: spaStore,
    })

    // In SPA preview-notification component is mounted within spa App.vue, not in ctp template.
    // tslint:disable-next-line:no-unused-expression
    new Vue({
      el: '#vue-preview-notification',
      store: spaStore,
      router: previewNotificationRouter,
    })

    Initiator.initCollapse()
    Initiator.initDrawer()
    Initiator.initSetting()
    Initiator.initPageToTop()
    Initiator.initNavTab()

    Initiator.initTopSlider()
    Initiator.initSlideShow()
    Initiator.initEventTracking()
    Initiator.initTopBrandList()
    Initiator.initSplitContent()

    await commonInitializer()

    Initiator.closeSpinner()
  }

  Initiator.publishPromotionContext()

  function isOnSpa(): boolean {
    return /^\/(s|hlapp)((\/?$)|\/[^\/]*)/.test(location.pathname)
  }
  async function commonInitializer() {
    Initiator.initDialogMigration()

    /**
     * 現状はinitAjaxはすべてのページで実行されているはずなのでここでviewportの設定も行っているが、
     * 意味的にはajaxがロードされないページでも実行されるべき処理である。
     * initAjaxがすべてのページで実行されるという前提が崩れる、または、この依存関係を解消する場合、
     * すべてのページで共通で実行されるべき処理群を定義し、そこに移動するなどの対応を検討すること。
     */
    Initiator.initViewPort()

    const replaceReferralUrl = await buildReplaceReferralUrl()
    replaceReferralUrl.resolveNode(document.body)
  }
}

initAjax({
  isHlAppWebViewUa: HlAppMode.isViewedInHlAppWebView,
})
